// General Conditions tab — duration-driven site costs with per-item qty overrides.
import { CalendarClock, ClipboardList, RotateCcw } from "lucide-react";
import TabSkeleton from "./TabSkeleton";
import { useWorkspace } from "../../../hooks/useEstimateWorkspace";
import { Card, CardHeader, IconButton, NumberInput, TD, TD_NUM, TH } from "../../../components/ui";
import { fmtCurrency, fmtQty } from "../../../utils/format";

export default function GenConditionsTab() {
  const { calc, isLocked, setOverride } = useWorkspace();
  if (!calc) return <TabSkeleton />;

  const { crew, durationDays } = calc.equipment;
  const { items, total, templateName } = calc.gc;

  return (
    <div className="space-y-4">
      <Card className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3" aria-label="Project duration">
        <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-text_primary">
          <CalendarClock size={15} className="text-primary" aria-hidden="true" />
          Project Duration = Interior + Exterior days
        </span>
        <span className="text-[13px] tabular-nums text-text_secondary">
          {fmtQty(crew.intDays)} int + {fmtQty(crew.extDays)} ext →{" "}
          <span className="font-semibold text-text_primary">{durationDays} days</span>
        </span>
        <span className="text-[11.5px] text-text_muted">Read-only — change crew sizes on the Equipment tab</span>
      </Card>

      <Card>
        <CardHeader
          icon={ClipboardList}
          title="General Conditions"
          subtitle={`Template: ${templateName} · Day/Week/Month items scale with duration`}
          actions={<span className="text-[12.5px] font-semibold tabular-nums text-text_primary">{fmtCurrency(total)}</span>}
        />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" aria-label="General conditions">
            <thead>
              <tr>
                <th className={TH}>Item</th>
                <th className={TH}>Basis</th>
                <th className={`${TH} text-right`}>Rate</th>
                <th className={`${TH} text-right`}>Qty override</th>
                <th className={TH}>Units</th>
                <th className={`${TH} text-right`}>Cost</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id}>
                  <td className={`${TD} font-medium`}>{it.item}</td>
                  <td className={TD}>{it.basis}</td>
                  <td className={TD_NUM}>{fmtCurrency(it.rate)}</td>
                  <td className={TD_NUM}>
                    <div className="flex items-center justify-end gap-1.5">
                      <NumberInput
                        value={it.overridden ? it.qty : ""}
                        placeholder={String(it.computedQty)}
                        min={0}
                        disabled={isLocked}
                        onChange={(v) => setOverride("gc", it.id, v === "" ? null : Math.max(0, v))}
                        className={`!w-20 ${it.overridden ? "text-info font-semibold" : ""}`}
                        aria-label={`${it.item} quantity override`}
                      />
                      {it.overridden && !isLocked ? (
                        <IconButton icon={RotateCcw} size="xs" variant="ghost" label={`Reset ${it.item} to ${it.computedQty}`} onClick={() => setOverride("gc", it.id, null)} />
                      ) : (
                        <span className="w-7" aria-hidden="true" />
                      )}
                    </div>
                  </td>
                  <td className={`${TD} text-text_secondary`}>
                    {it.qty} {it.units}
                  </td>
                  <td className={TD_NUM}>{fmtCurrency(it.cost)}</td>
                </tr>
              ))}
              <tr className="bg-surface_alt">
                <td className={`${TD} font-semibold`} colSpan={5}>
                  Total General Conditions
                </td>
                <td className={`${TD_NUM} font-semibold`}>{fmtCurrency(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
